/**
 * MusicPlaylistsPage - 音楽ライブラリの「プレイリスト」一覧
 *
 * プレイリストはサーバー（user_playlists）に保存され、更新が新しい順に並ぶ。
 */

import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { createMusicPlaylist, getMusicPlaylists } from '@/api/api'
import type { MusicPlaylistItem } from '@/api/type'
import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { PATHS } from '@/routes/paths'

import { ACCENT_COLOR } from './constants'
import { PlaylistRow } from './playlist-row'

export const MusicPlaylistsPage = () => {
  const navigate = useNavigate()

  const [playlists, setPlaylists] = useState<MusicPlaylistItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const { isSubmitting, run } = useSubmitLock()

  useEffect(() => {
    let cancelled = false
    getMusicPlaylists()
      .then((res) => {
        if (!cancelled) setPlaylists(res.playlists)
      })
      .catch(() => {
        if (!cancelled) setError('プレイリストの取得に失敗しました')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleCreate = () => {
    const name = newName.trim()
    if (!name) return

    void run(async () => {
      try {
        const playlist = await createMusicPlaylist(name)
        setPlaylists((prev) => [playlist, ...prev])
        setNewName('')
        setIsCreating(false)
      } catch {
        toast.error('プレイリストの作成に失敗しました')
      }
    })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header title="プレイリスト" onClose={() => navigate(PATHS.MUSIC.LIBRARY)} />

      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        {isCreating ? (
          <div className="flex gap-2 py-3">
            <Input
              autoFocus
              value={newName}
              maxLength={200}
              placeholder="プレイリスト名"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <Button disabled={!newName.trim() || isSubmitting} onClick={handleCreate}>
              作成
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            className="flex w-full items-center gap-3 border-none py-4 text-left"
            style={{ color: ACCENT_COLOR }}
          >
            <Plus size={22} className="shrink-0" />
            新規プレイリスト
          </button>
        )}

        {isLoading && (
          <div className="flex justify-center py-12">
            <Spinner size={40} />
          </div>
        )}
        {!isLoading && error && <div className="py-12 text-center text-muted-foreground">{error}</div>}
        {!isLoading && !error && playlists.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">プレイリストがありません</div>
        )}
        {!isLoading &&
          !error &&
          playlists.map((playlist) => (
            <PlaylistRow
              key={playlist.id}
              playlist={playlist}
              onClick={() => navigate(PATHS.MUSIC.PLAYLIST_DETAIL.replace(':playlist_id', String(playlist.id)))}
            />
          ))}
      </div>
    </div>
  )
}
